import {Container, ContainerModule, type interfaces} from "inversify";
import {Bindings} from "data/constants/bindings";
import {type IUserStore, UserStore} from "data/stores/user/user.store";
import {AuthController, type IAuthController} from "views/controllers/auth/auth.controller";
import {
	type IMyAccountController,
	MyAccountController,
} from "views/pages/my_account/my_account.controller";
import {type ILoginController, LoginController} from "views/pages/login/login.controller";
import {API_URL, CONTENT_JSON_URL, JSON_URL} from "data/constants";
import {type IJSONProvider, JSONProvider} from "data/providers/json/json.provider";
import {AuthApiProvider, type IAuthApiProvider} from "data/providers/api/auth.api.provider";
import {HttpClientFactory, type IHttpClientService} from "data/services/http";
import {CountriesStore, type ICountriesStore} from "data/stores/countries/countries.store";
import {RoundsStore, type IRoundsStore} from "data/stores/rounds/rounds.store";
import {PlayersStore, type IPlayersStore} from "data/stores/players/players.store";
import {SquadsStore, type ISquadsStore} from "data/stores/squads/squds.store";
import {
	type ISecretGateController,
	SecretGateController,
} from "views/controllers/secrete_gate/secret_gate.controller";
import {
	type ILocalizationStore,
	LocalizationStore,
} from "data/stores/localization/localization.store";
import {
	type IStaticContentStore,
	StaticContentStore,
} from "data/stores/static_content/static_content.store";
import {HelpController, type IHelpController} from "views/pages/help/help.controller";
import {
	HelpListController,
	type IHelpListController,
} from "views/components/help/help_list/help_list.controller";
import {
	ContactUsController,
	type IContactUsController,
} from "views/components/help/contact_us/contact_us.controller";
import {
	type ILiveScoreController,
	LiveScoreController,
} from "views/controllers/live_score/live_score.controller";
import {ChecksumStore, type IChecksumStore} from "data/stores/checksum/checksum.store";
import {
	type IRegistrationController,
	RegistrationController,
} from "views/pages/registration/registration.controller";
import {type IUserApiProvider, UserApiProvider} from "data/providers/api/user.api.provider";
import {
	type IPasswordApiProvider,
	PasswordApiProvider,
} from "data/providers/api/password.api.provider";
import {
	ForgotPasswordController,
	type IForgotPasswordController,
} from "views/pages/forgot_password/forgot_password.controller";
import {
	type IResetPasswordController,
	ResetPasswordController,
} from "views/pages/reset_password/reset_password.controller";
import {
	type ISessionController,
	SessionController,
} from "views/components/session/session.controller";
import {
	BootstrapController,
	type IBootstrapController,
} from "views/components/bootstrap/bootstrap.controller";
import {
	type ILeaguesApiProvider,
	LeaguesApiProvider,
} from "data/providers/api/leagues.api.provider";
import {type ILeaguesStore, LeaguesStore} from "data/stores/leagues/leagues.store";
import {
	type IMyLeaguesController,
	MyLeaguesController,
} from "views/pages/my_leagues/my_leagues.controller";
import {
	type IJoinLeaguesController,
	JoinLeaguesController,
} from "views/pages/join_leagues/join_leagues.controller";
import {type ILeagueController, LeagueController} from "views/pages/league/league.controller";
import {
	type ILeagueSettingsController,
	LeagueSettingsController,
} from "views/pages/league_settings/league_settings.controller";
import {
	type ILeagueAboutController,
	LeagueAboutController,
} from "views/pages/league_about/league_about.controller";
import {type IModalsStore, ModalsStore} from "data/stores/modals/modals.store";
import {IStatesStore, StatesStore} from "data/stores/states/states.store";
import {IMenuStore, MenuStore} from "data/stores/menu/menu.store";
import {HeaderController, IHeaderController} from "views/components/header/header.controller";
import {
	ILocalizationController,
	LocalizationController,
} from "views/controllers/localization/localization.controller";
import {
	CookieBannerController,
	ICookieBannerController,
} from "views/components/CookieBanner/cookie_banner.controller";
import {IMenuController, MenuController} from "views/components/menu/menu.controller";
import {
	GlobalModalsController,
	IGlobalModalsController,
} from "views/components/modals/global_modals.controller";
import {IModalsController, ModalsController} from "views/controllers/modals/modals.controller";
import {GameController, IGameController} from "views/pages/game/game.controller";
import {IPinpointApiProvider, PinpointApiProvider} from "data/providers/api/pinpoint.api.provider";
import {IPinpointStore, PinpointStore} from "data/stores/pinpoint/pinpoint.store";
import {IRoundController, RoundController} from "views/components/round/round.controller";
import {ILandingController, LandingController} from "views/pages/landing/landing.controller";
import {IPickPageController, PickPageController} from "views/pages/pick/pick.controller";
import {IPickController, PickController} from "views/components/pick/pick.controller";
import {
	IPlayerSlotController,
	PlayerSlotController,
} from "views/components/player_slot/player_slot.controller";
import {IPlayerPoolStore, PlayerPoolStore} from "data/stores/player_pool/player_pool.store";
import {
	IPoolController,
	PoolController,
} from "views/components/player_pool/pool/player_pool.controller";
import {
	IPoolModalController,
	PoolModalController,
} from "views/components/player_pool/pool_modal/pool_modal.controller";
import {
	IPlayerInfoController,
	PlayerInfoController,
} from "views/components/player_info/player_info.controller";
import {
	IRoundResultsController,
	RoundResultsController,
} from "views/components/round_results/round_results.controller";
import {IPrizesController, PrizesController} from "views/pages/prizes/prizes.controller";
import {
	IPlayedGamesController,
	PlayedGamesController,
} from "views/components/modals/played_game/played_game.controller";
import {IShareController, ShareController} from "views/controllers/share/share.controller";
import {IShareStore, ShareStore} from "data/stores/share/share.store";
import {
	IPredictModalController,
	PredictModalController,
} from "views/components/modals/predict_modal/predict_modal.controller";
import {FooterController, IFooterController} from "views/components/footer/footer.controller";

export const DIContainer = new Container();

export const services = new ContainerModule((bind: interfaces.Bind) => {
	bind<IHttpClientService>(Bindings.ApiHTTPClient).toConstantValue(
		HttpClientFactory.createApiClient({
			baseURL: API_URL,
			withCredentials: true,
		})
	);

	bind<IHttpClientService>(Bindings.JsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: JSON_URL,
		})
	);

	bind<IHttpClientService>(Bindings.ContentJsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: CONTENT_JSON_URL,
		})
	);
});

export const providers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IJSONProvider>(Bindings.JSONProvider).to(JSONProvider);
	bind<IAuthApiProvider>(Bindings.AuthApiProvider).to(AuthApiProvider);
	bind<IUserApiProvider>(Bindings.UserApiProvider).to(UserApiProvider);
	bind<IPasswordApiProvider>(Bindings.PasswordApiProvider).to(PasswordApiProvider);
	bind<ILeaguesApiProvider>(Bindings.LeaguesApiProvider).to(LeaguesApiProvider);
	bind<IPinpointApiProvider>(Bindings.PinpointApiProvider).to(PinpointApiProvider);
});

export const stores = new ContainerModule((bind: interfaces.Bind) => {
	bind<ILocalizationStore>(Bindings.LocalizationStore).to(LocalizationStore).inSingletonScope();
	bind<IUserStore>(Bindings.UserStore).to(UserStore).inSingletonScope();
	bind<IRoundsStore>(Bindings.RoundsStore).to(RoundsStore).inSingletonScope();
	bind<IPlayersStore>(Bindings.PlayersStore).to(PlayersStore).inSingletonScope();
	bind<ISquadsStore>(Bindings.SquadsStore).to(SquadsStore).inSingletonScope();
	bind<IMenuStore>(Bindings.MenuStore).to(MenuStore).inSingletonScope();

	bind<ICountriesStore>(Bindings.CountriesStore).to(CountriesStore);
	bind<IStatesStore>(Bindings.StatesStore).to(StatesStore);
	bind<IStaticContentStore>(Bindings.StaticContentStore).to(StaticContentStore);

	bind<IChecksumStore>(Bindings.ChecksumStore).to(ChecksumStore);
	bind<ILeaguesStore>(Bindings.LeaguesStore).to(LeaguesStore).inSingletonScope();
	bind<IModalsStore>(Bindings.ModalsStore).to(ModalsStore).inSingletonScope();
	bind<IPinpointStore>(Bindings.PinpointStore).to(PinpointStore).inSingletonScope();
	bind<IPlayerPoolStore>(Bindings.PlayerPoolStore).to(PlayerPoolStore).inSingletonScope();
	bind<IShareStore>(Bindings.ShareStore).to(ShareStore).inSingletonScope();
});

export const controllers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IAuthController>(Bindings.AuthController).to(AuthController);
	bind<ILocalizationController>(Bindings.LocalizationController).to(LocalizationController);
	bind<ILoginController>(Bindings.LoginController).to(LoginController);
	bind<IForgotPasswordController>(Bindings.ForgotPasswordController).to(ForgotPasswordController);
	bind<IResetPasswordController>(Bindings.ResetPasswordController).to(ResetPasswordController);
	bind<IRegistrationController>(Bindings.RegistrationController).to(RegistrationController);
	bind<IMyAccountController>(Bindings.MyAccountController).to(MyAccountController);
	bind<ISecretGateController>(Bindings.SecretGateController).to(SecretGateController);
	bind<IHelpController>(Bindings.HelpController).to(HelpController);
	bind<IHelpListController>(Bindings.HelpListController).to(HelpListController);
	bind<IContactUsController>(Bindings.ContactUsController).to(ContactUsController);
	bind<ILiveScoreController>(Bindings.LiveScoreController).to(LiveScoreController);
	bind<ISessionController>(Bindings.SessionController).to(SessionController);
	bind<IBootstrapController>(Bindings.BootstrapController).to(BootstrapController);
	bind<IHeaderController>(Bindings.HeaderController).to(HeaderController);
	bind<IMyLeaguesController>(Bindings.MyLeaguesController).to(MyLeaguesController);
	bind<IJoinLeaguesController>(Bindings.JoinLeaguesController).to(JoinLeaguesController);
	bind<ILeagueController>(Bindings.LeagueController).to(LeagueController);
	bind<ILeagueSettingsController>(Bindings.LeagueSettingsController).to(LeagueSettingsController);
	bind<ILeagueAboutController>(Bindings.LeagueAboutController).to(LeagueAboutController);
	bind<IGlobalModalsController>(Bindings.GlobalModalsController).to(GlobalModalsController);
	bind<IModalsController>(Bindings.ModalsController).to(ModalsController);
	bind<ICookieBannerController>(Bindings.CookieBannerController).to(CookieBannerController);
	bind<IMenuController>(Bindings.MenuController).to(MenuController);
	bind<IGameController>(Bindings.GameController).to(GameController);
	bind<IRoundController>(Bindings.RoundController).to(RoundController);
	bind<ILandingController>(Bindings.LandingController).to(LandingController);
	bind<IPickPageController>(Bindings.PickPageController).to(PickPageController);
	bind<IPickController>(Bindings.PickController).to(PickController);
	bind<IPlayerSlotController>(Bindings.PlayerSlotController).to(PlayerSlotController);
	bind<IPoolController>(Bindings.PoolController).to(PoolController);
	bind<IPoolModalController>(Bindings.PoolModalController).to(PoolModalController);
	bind<IPlayerInfoController>(Bindings.PlayerInfoController).to(PlayerInfoController);
	bind<IRoundResultsController>(Bindings.RoundResultsController).to(RoundResultsController);
	bind<IPrizesController>(Bindings.PrizesController).to(PrizesController);
	bind<IPlayedGamesController>(Bindings.PlayedGamesController).to(PlayedGamesController);
	bind<IShareController>(Bindings.ShareController).to(ShareController);
	bind<IPredictModalController>(Bindings.PredictModalController).to(PredictModalController);
	bind<IFooterController>(Bindings.FooterController).to(FooterController);
});
